import { useEffect, useState, type FC, type ReactNode } from "react";
import { Column } from "primereact/column";
import { DataTable, type DataTablePageEvent } from "primereact/datatable";
import { Dropdown, type DropdownChangeEvent } from "primereact/dropdown";
import DataTableCard from "@/components/DataTableCard/DataTableCard";
import styles from "@/components/DataTableCard/DataTableCard.module.css";
import { TEXT } from "@/constants/text";
import type { PerformanceReportRow } from "@/types/reporting";
import {
  CSV_ESCAPE,
  RPP_OPTIONS,
  currencyFormatter,
  cpmFormatter,
  fmt,
  fmtPct,
  fmtStr,
  integerFormatter,
} from "@/utils/tableFormatters";

const ADVERTISER_BODY = ({
  advertiser_name: advertiserName,
}: PerformanceReportRow): ReactNode => (
  <span title={advertiserName}>{fmtStr(advertiserName)}</span>
);
const CAMPAIGN_BODY = ({
  campaign_name: campaignName,
}: PerformanceReportRow): ReactNode => (
  <span title={campaignName}>{fmtStr(campaignName)}</span>
);
const STRATEGY_GROUP_BODY = ({
  strategy_group_name: strategyGroupName,
}: PerformanceReportRow): ReactNode => (
  <span title={strategyGroupName}>{fmtStr(strategyGroupName)}</span>
);

interface PerformanceTableProps {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
}

const TABLE_TEXT = TEXT.campaignOverview.performanceTable;

const FILTER_FIELDS = [
  "advertiser_name",
  "campaign_name",
  "strategy_group_name",
] as const;

const PerformanceTable: FC<PerformanceTableProps> = ({
  rows,
  isLoading,
  error,
}) => {
  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(25);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    setFirst(0);
  }, [rows]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(e.target.value);
    setFirst(0);
  };

  const handlePage = (e: DataTablePageEvent) => {
    setFirst(e.first);
  };

  const handleRowsChange = (e: DropdownChangeEvent) => {
    setPageRows(e.value as number);
    setFirst(0);
  };

  const handleExportCsv = () => {
    const term = globalFilter.trim().toLowerCase();
    const exportRows = term
      ? rows.filter((row) =>
          FILTER_FIELDS.some((f) => {
            const val = row[f];
            return typeof val === "string" && val.toLowerCase().includes(term);
          })
        )
      : rows;

    const headers = [
      TABLE_TEXT.columnAdvertiser,
      TABLE_TEXT.columnCampaign,
      TABLE_TEXT.columnStrategyGroup,
      TABLE_TEXT.columnImpressions,
      TABLE_TEXT.columnClicks,
      TABLE_TEXT.columnCtr,
      TABLE_TEXT.columnVideoCompletes,
      TABLE_TEXT.columnVcr,
      TABLE_TEXT.columnConversions,
      TABLE_TEXT.columnCpa,
      TABLE_TEXT.columnEcpm,
    ].join(",");

    const body = exportRows
      .map((row) =>
        [
          CSV_ESCAPE(fmtStr(row.advertiser_name)),
          CSV_ESCAPE(fmtStr(row.campaign_name)),
          CSV_ESCAPE(fmtStr(row.strategy_group_name)),
          CSV_ESCAPE(fmt(row.impressions, integerFormatter)),
          CSV_ESCAPE(fmt(row.clicks, integerFormatter)),
          CSV_ESCAPE(fmtPct(row.ctr)),
          CSV_ESCAPE(fmt(row.video_complete, integerFormatter)),
          CSV_ESCAPE(fmtPct(row.video_complete_rate)),
          CSV_ESCAPE(fmt(row.total_conversions, integerFormatter)),
          CSV_ESCAPE(fmt(row.total_spend_cpa, currencyFormatter)),
          CSV_ESCAPE(fmt(row.total_spend_cpm, cpmFormatter)),
        ].join(",")
      )
      .join("\n");

    const blob = new Blob([`\uFEFF${headers}\n${body}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "performance-table.csv";
    anchor.click();
    // Defer revoke so the browser has time to initiate the async blob fetch.
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <DataTableCard
      csvButtonDisabled={isLoading || Boolean(error) || rows.length === 0}
      csvButtonLabel={TABLE_TEXT.csvButton}
      error={error ? TABLE_TEXT.error : null}
      filterPlaceholder={TABLE_TEXT.filterPlaceholder}
      globalFilter={globalFilter}
      isLoading={isLoading}
      title={TABLE_TEXT.title}
      onExportCsv={handleExportCsv}
      onFilterChange={handleFilterChange}
    >
      <DataTable
        className={styles.table}
        columnResizeMode="fit"
        currentPageReportTemplate="{first}–{last} of {totalRecords}"
        emptyMessage={TABLE_TEXT.emptyMessage}
        first={first}
        globalFilter={globalFilter}
        globalFilterFields={[...FILTER_FIELDS]}
        paginator
        paginatorLeft={
          <div className={styles.rppGroup}>
            <label htmlFor="perf-rpp">{TABLE_TEXT.rowsPerPage}</label>
            <Dropdown
              className={styles.rppDropdown}
              inputId="perf-rpp"
              options={RPP_OPTIONS}
              pt={{ panel: { className: styles.rppDropdownPanel } }}
              value={pageRows}
              onChange={handleRowsChange}
            />
          </div>
        }
        paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
        pt={{
          bodyRow: { className: styles.tableRow },
          header: { className: styles.columnHeader },
        }}
        removableSort
        resizableColumns
        rows={pageRows === 0 ? Math.max(rows.length, 1) : pageRows}
        scrollable
        scrollHeight="28rem"
        sortMode="single"
        tableStyle={{ tableLayout: "fixed" }}
        value={rows}
        onPage={handlePage}
      >
        <Column
          body={ADVERTISER_BODY}
          field="advertiser_name"
          header={TABLE_TEXT.columnAdvertiser}
          sortable
          style={{ width: "9%" }}
        />
        <Column
          body={CAMPAIGN_BODY}
          field="campaign_name"
          header={TABLE_TEXT.columnCampaign}
          sortable
          style={{ width: "13%" }}
        />
        <Column
          body={STRATEGY_GROUP_BODY}
          field="strategy_group_name"
          header={TABLE_TEXT.columnStrategyGroup}
          sortable
          style={{ width: "11%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.impressions, integerFormatter)
          }
          field="impressions"
          header={TABLE_TEXT.columnImpressions}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "8%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.clicks, integerFormatter)
          }
          field="clicks"
          header={TABLE_TEXT.columnClicks}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "6%" }}
        />
        <Column
          body={(row: PerformanceReportRow) => fmtPct(row.ctr)}
          field="ctr"
          header={TABLE_TEXT.columnCtr}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "6%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.video_complete, integerFormatter)
          }
          field="video_complete"
          header={TABLE_TEXT.columnVideoCompletes}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "8%" }}
        />
        <Column
          body={(row: PerformanceReportRow) => fmtPct(row.video_complete_rate)}
          field="video_complete_rate"
          header={TABLE_TEXT.columnVcr}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "5%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.total_conversions, integerFormatter)
          }
          field="total_conversions"
          header={TABLE_TEXT.columnConversions}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "7%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.total_spend_cpa, currencyFormatter)
          }
          field="total_spend_cpa"
          header={TABLE_TEXT.columnCpa}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "7%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.total_spend_cpm, cpmFormatter)
          }
          field="total_spend_cpm"
          header={TABLE_TEXT.columnEcpm}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "6%" }}
        />
      </DataTable>
    </DataTableCard>
  );
};

export default PerformanceTable;
